import { HttpErrorResponse } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { FileOpener } from '@capacitor-community/file-opener';
import { Observable, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import type { NegocioService } from '../services/negocio.service';
import type { VisitasService } from '../services/visitas.service';
import type { ToastService } from '../services/toast.service';

export interface AdjuntoParaAbrir {
  ruta_almacenamiento: string;
  nombre_archivo?: string | null;
  mime_type?: string | null;
  url_descarga?: string | null;
}

interface ArchivoPersistido {
  path: string;
  uri: string;
}

/** Infiere MIME por extensión cuando el backend o el Blob no lo envían. */
export function mimeInferidoPorNombreArchivo(nombre: string): string {
  const n = (nombre ?? '').toLowerCase();
  if (n.endsWith('.pdf')) return 'application/pdf';
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.gif')) return 'image/gif';
  if (n.endsWith('.csv')) return 'text/csv';
  if (n.endsWith('.doc')) return 'application/msword';
  if (n.endsWith('.docx')) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }
  if (n.endsWith('.xls')) return 'application/vnd.ms-excel';
  if (n.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  return '';
}

function esImagen(mime: string): boolean {
  return mime.startsWith('image/');
}

function esPdf(mime: string): boolean {
  return mime === 'application/pdf';
}

function sanitizarNombreArchivo(nombre: string): string {
  const base = (nombre ?? '').trim() || 'adjunto';
  return base.replace(/[/\\?%*:|"<>]/g, '_').slice(0, 180);
}

function blobABase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('No se pudo convertir el archivo'));
        return;
      }
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64 ?? '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('Error al leer el archivo'));
    reader.readAsDataURL(blob);
  });
}

async function asegurarDirectorioAdjuntos(): Promise<void> {
  try {
    await Filesystem.mkdir({
      path: 'adjuntos',
      directory: Directory.Cache,
      recursive: true,
    });
  } catch {
    // Ya existe o el plugin lo crea al escribir.
  }
}

/** Guarda el blob en caché y devuelve path + URI nativa para Share/FileOpener. */
async function persistirBlobEnCache(blob: Blob, nombreArchivo: string): Promise<ArchivoPersistido> {
  const base64 = await blobABase64(blob);
  const nombreSeguro = sanitizarNombreArchivo(nombreArchivo);
  const path = `adjuntos/${Date.now()}_${nombreSeguro}`;

  await asegurarDirectorioAdjuntos();
  await Filesystem.writeFile({
    path,
    data: base64,
    directory: Directory.Cache,
  });

  const { uri } = await Filesystem.getUri({
    path,
    directory: Directory.Cache,
  });

  return { path, uri };
}

/** Abre la hoja Compartir del sistema para guardar o enviar el archivo. */
async function compartirArchivoNativo(uri: string, nombre: string): Promise<void> {
  await Share.share({
    title: nombre,
    dialogTitle: 'Guardar archivo',
    files: [uri],
  });
}

function esCancelacionShare(error: unknown): boolean {
  const msg = String((error as { message?: string })?.message ?? error ?? '').toLowerCase();
  return msg.includes('cancel') || msg.includes('dismiss') || msg.includes('closed');
}

async function descargarBlobNativo(
  blob: Blob,
  nombreArchivo: string,
  toastService?: ToastService
): Promise<void> {
  try {
    const { uri } = await persistirBlobEnCache(blob, nombreArchivo);
    await compartirArchivoNativo(uri, nombreArchivo);
    if (toastService) {
      await toastService.mostrar('Seleccione dónde guardar el archivo.', 'success');
    }
  } catch (error) {
    if (esCancelacionShare(error)) return;
    if (toastService) {
      await toastService.mostrar('No se pudo compartir el archivo.', 'error');
    }
  }
}

async function abrirConFileOpener(
  blob: Blob,
  nombreArchivo: string,
  mime: string,
  toastService?: ToastService
): Promise<void> {
  try {
    const { uri } = await persistirBlobEnCache(blob, nombreArchivo);
    await FileOpener.open({
      filePath: uri,
      contentType: mime || 'application/octet-stream',
      openWithDefault: Capacitor.getPlatform() !== 'android',
    });
  } catch (error) {
    if (toastService) {
      await toastService.mostrar(
        'No se pudo abrir el archivo. Verifique que haya una aplicación compatible instalada.',
        'error'
      );
    }
    throw error;
  }
}

function crearMensajeSinPreview(texto: string): HTMLParagraphElement {
  const p = document.createElement('p');
  p.className = 'adjunto-visor-inapp-sin-preview';
  p.textContent = texto;
  return p;
}

/** Base de la app (href de `<base>`), siempre con barra final. */
export function baseHrefApp(): string {
  const base = document.querySelector('base')?.href ?? '/';
  return base.endsWith('/') ? base : `${base}/`;
}

/** URL pública bajo `/assets/pdfjs/...` (subruta sin barra inicial). */
export function urlAssetPdfJs(subruta: string): string {
  const rel = subruta.replace(/^\/+/, '');
  return `${baseHrefApp()}assets/pdfjs/${rel}`;
}

function workerSrcPdfJs(): string {
  return urlAssetPdfJs('pdf.worker.min.mjs');
}

/** Opciones de `getDocument` compatibles con pdfjs-dist 6 (fuentes/wasm/cmaps). */
export function opcionesGetDocumentPdfJs(data: ArrayBuffer): {
  data: ArrayBuffer;
  standardFontDataUrl: string;
  wasmUrl: string;
  cMapUrl: string;
  cMapPacked: true;
} {
  return {
    data,
    standardFontDataUrl: urlAssetPdfJs('standard_fonts/'),
    wasmUrl: urlAssetPdfJs('wasm/'),
    cMapUrl: urlAssetPdfJs('cmaps/'),
    cMapPacked: true,
  };
}

type PdfJsModule = typeof import('pdfjs-dist');
type PdfJsLoadingTask = ReturnType<PdfJsModule['getDocument']>;

/** Renderiza un PDF con pdf.js dentro del contenedor; devuelve false si falla. */
export async function renderizarPdfEnContenedor(
  blob: Blob,
  container: HTMLElement,
  cargarPdfJs: () => Promise<PdfJsModule> = () => import('pdfjs-dist')
): Promise<boolean> {
  let loadingTask: PdfJsLoadingTask | null = null;
  try {
    const pdfjs = await cargarPdfJs();
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrcPdfJs();

    const arrayBuffer = await blob.arrayBuffer();
    loadingTask = pdfjs.getDocument(opcionesGetDocumentPdfJs(arrayBuffer));
    const pdf = await loadingTask.promise;

    container.innerHTML = '';
    const scroll = document.createElement('div');
    scroll.className = 'adjunto-visor-inapp-pdf-scroll';

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1.35 });
      const canvas = document.createElement('canvas');
      canvas.className = 'adjunto-visor-inapp-pdf-pagina';
      const context = canvas.getContext('2d');
      if (!context) continue;

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({ canvasContext: context, viewport, canvas }).promise;
      scroll.appendChild(canvas);
    }

    container.appendChild(scroll);
    return scroll.childElementCount > 0;
  } catch {
    return false;
  } finally {
    try {
      await loadingTask?.destroy();
    } catch {
      // Ignorar fallos al liberar el worker.
    }
  }
}

function abrirODescargarBlobWeb(blob: Blob, nombreArchivo: string, mime: string): void {
  const url = URL.createObjectURL(blob);
  const esVisualizable = esImagen(mime) || esPdf(mime);
  if (esVisualizable) {
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreArchivo;
    a.click();
    URL.revokeObjectURL(url);
  }
}

function crearBotonToolbar(clase: string, texto: string, ariaLabel: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = clase;
  btn.textContent = texto;
  btn.setAttribute('aria-label', ariaLabel);
  return btn;
}

/**
 * Modal nativo unificado: preview (imagen/PDF), mensaje sin preview (CSV, etc.),
 * botones Descargar (Share) y Cerrar.
 */
function abrirVisorInApp(
  blob: Blob,
  nombreArchivo: string,
  mime: string,
  toastService?: ToastService
): void {
  const url = esImagen(mime) ? URL.createObjectURL(blob) : null;
  const overlay = document.createElement('div');
  overlay.className = 'adjunto-visor-inapp';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', nombreArchivo);

  const toolbar = document.createElement('div');
  toolbar.className = 'adjunto-visor-inapp-toolbar';

  const titulo = document.createElement('span');
  titulo.className = 'adjunto-visor-inapp-titulo';
  titulo.textContent = nombreArchivo;

  const acciones = document.createElement('div');
  acciones.className = 'adjunto-visor-inapp-acciones';

  const descargar = crearBotonToolbar(
    'adjunto-visor-inapp-descargar',
    'Descargar',
    'Descargar archivo'
  );
  const cerrar = crearBotonToolbar('adjunto-visor-inapp-cerrar', 'Cerrar', 'Cerrar visor');

  const contenido = document.createElement('div');
  contenido.className = 'adjunto-visor-inapp-contenido';

  const limpiar = (): void => {
    overlay.remove();
    if (url) URL.revokeObjectURL(url);
  };

  cerrar.addEventListener('click', limpiar);
  descargar.addEventListener('click', () => {
    void descargarBlobNativo(blob, nombreArchivo, toastService);
  });

  if (esImagen(mime) && url) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = nombreArchivo;
    img.className = 'adjunto-visor-inapp-imagen';
    contenido.appendChild(img);
  } else if (esPdf(mime)) {
    const cargando = crearMensajeSinPreview('Cargando vista previa…');
    contenido.appendChild(cargando);

    void renderizarPdfEnContenedor(blob, contenido).then((ok) => {
      if (ok) return;

      contenido.innerHTML = '';
      contenido.appendChild(
        crearMensajeSinPreview(
          'No se pudo mostrar la vista previa del PDF. Use Descargar o abra el archivo con otra aplicación.'
        )
      );

      const abrirExterno = crearBotonToolbar(
        'adjunto-visor-inapp-abrir-externo',
        'Abrir con otra app',
        'Abrir PDF con otra aplicación'
      );
      abrirExterno.addEventListener('click', () => {
        void abrirConFileOpener(blob, nombreArchivo, mime, toastService);
      });
      acciones.insertBefore(abrirExterno, descargar);
    });
  } else {
    contenido.appendChild(
      crearMensajeSinPreview(
        'Este archivo no tiene vista previa. Use Descargar para guardarlo en el teléfono.'
      )
    );
  }

  acciones.appendChild(descargar);
  acciones.appendChild(cerrar);
  toolbar.appendChild(titulo);
  toolbar.appendChild(acciones);
  overlay.appendChild(toolbar);
  overlay.appendChild(contenido);
  document.body.appendChild(overlay);
}

async function abrirODescargarBlobNativo(
  blob: Blob,
  nombreArchivo: string,
  mime: string,
  toastService?: ToastService
): Promise<void> {
  if (!blob?.size) {
    if (toastService) {
      await toastService.mostrar('El archivo está vacío o no se pudo descargar.', 'warning');
    }
    return;
  }

  abrirVisorInApp(blob, nombreArchivo, mime, toastService);
}

/**
 * Descarga o comparte un blob: en web usa enlace de descarga; en nativo abre la hoja Compartir.
 * Usar para PDFs generados en cliente (jsPDF) u otros archivos que deban guardarse sin visor.
 */
export async function descargarOCompartirBlob(
  blob: Blob,
  nombreArchivo: string,
  toastService?: ToastService
): Promise<void> {
  const nombre = (nombreArchivo ?? '').trim() || 'archivo';
  if (!blob?.size) {
    if (toastService) {
      await toastService.mostrar('El archivo está vacío o no se pudo generar.', 'warning');
    }
    return;
  }
  if (Capacitor.isNativePlatform()) {
    await descargarBlobNativo(blob, nombre, toastService);
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Abre o descarga un blob: en web usa pestaña/descarga; en nativo modal unificado con Share.
 */
export function abrirODescargarBlob(
  blob: Blob,
  nombreArchivo: string,
  mimeOpcional?: string | null,
  toastService?: ToastService
): void {
  const nombre = (nombreArchivo ?? '').trim() || 'adjunto';
  const mime = (mimeOpcional || blob.type || mimeInferidoPorNombreArchivo(nombre)).toLowerCase();

  if (Capacitor.isNativePlatform()) {
    void abrirODescargarBlobNativo(blob, nombre, mime, toastService);
    return;
  }
  abrirODescargarBlobWeb(blob, nombre, mime);
}

/**
 * Abre o descarga un adjunto vía GET `/api/v1/archivos/{file_path}`; fallback a `url_descarga` si 404.
 */
export function abrirODescargarAdjunto(
  adjunto: AdjuntoParaAbrir,
  negocioService: NegocioService,
  visitasService: VisitasService | null,
  toastService: ToastService
): void {
  const filePath = adjunto?.ruta_almacenamiento?.trim();
  if (!filePath) {
    void toastService.mostrar('No hay archivo para visualizar.', 'warning');
    return;
  }
  const fallbackUrl = adjunto.url_descarga?.trim();
  const nombre = adjunto.nombre_archivo?.trim() || 'adjunto';

  let chain: Observable<Blob> = negocioService.obtenerArchivoPorRuta(filePath);
  if (visitasService && fallbackUrl) {
    chain = chain.pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404 && fallbackUrl) {
          return visitasService.descargarAdjunto(fallbackUrl);
        }
        return throwError(() => err);
      })
    );
  }

  chain.pipe(take(1)).subscribe({
    next: (blob) => {
      const mime = (adjunto.mime_type || blob.type || mimeInferidoPorNombreArchivo(nombre)).toLowerCase();
      abrirODescargarBlob(blob, nombre, mime, toastService);
    },
    error: () => {
      void toastService.mostrar('Error al obtener el archivo', 'error');
    },
  });
}
